// apps/client/src/hooks/useEdmCampaigns.ts
//
// Typed wrappers over the `edmCampaignAction` callable. Every request is
// `{ action, companyId, ...fields }`; every refusal is
// `HttpsError(code, message, { code: '<reason>' })`, mapped for display by
// utils/edm edmErrorMessage. Projections carry ISO strings (or null) and
// plain numbers, never Firestore Timestamps.
//
// eDM state is read ONLY through this callable. The edm_* collections are
// client read/write false in firestore.rules; there is no Firestore SDK read
// of them anywhere in the client.

import { useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { EdmCampaignStatus } from '../utils/edm';
import type { EdmBlock } from './useEdmContent';

/** The plan's callable timeout for eDM (a freeze can run for minutes). */
export const EDM_CALLABLE_OPTIONS = { timeout: 600_000 } as const;

export type EdmContentPath = 'fixed_layout' | 'custom_html';

export interface EdmFixedLayout {
  heading: string;
  intro: string;
  body: string;
  ctaLabel?: string;
  ctaUrl?: string;
  signOff: string;
}

export interface EdmAudience {
  includeLeads: boolean;
  includeClients: boolean;
  includeContacts: boolean;
  /** Contact tags (nameNorm values); filters imported contacts only. null = no filter. */
  contactTags?: string[] | null;
  /** Broker Auth UIDs (getBrokerLabels ids); null = every broker. */
  brokerUids?: string[] | null;
  /** edm_content: 'all' also applies `contactTags` to leads and clients. null/absent = contacts only. */
  tagsApplyTo?: 'contacts' | 'all' | null;
  /** edm_tracking: set by the server on a resend-to-non-openers child (read-only here). */
  resendOf?: string | null;
}

/** edm_tracking: A/B subject-line test settings. */
export interface EdmAbTest {
  subjectB: string;
  /** 10-50: share of the audience in the test cohort. */
  testPercent: number;
  /** 1-24: hours after the test cohort is sent before the winner is decided. */
  decideAfterHours: number;
}

export type EdmAbVariant = 'A' | 'B';

/** edm_tracking: unique-recipient engagement counts (approximate: image blocking, bots, privacy proxies). */
export interface EdmEngagement {
  uniqueOpens: number;
  uniqueClicks: number;
  links?: Array<{ url: string; uniqueClicks: number }>;
  byVariant?: Partial<Record<EdmAbVariant, { sent: number; uniqueOpens: number; uniqueClicks: number }>>;
  winner?: EdmAbVariant | null;
}

/** One row of a multi-campaign getStats (campaignIds). */
export type EdmCampaignStats = EdmStats & { campaignId: string };

/** One page of a paged audience count (edm_content). */
export interface EdmAudienceCountPage {
  counters: EdmAudienceCounters;
  done: boolean;
  cursor: { jobId: string; position: string } | null;
}

export interface EdmFreezeCounters {
  recipients: number;
  duplicates: number;
  suppressed: number;
  invalidEmail: number;
  missingFirstName: number;
  excludedUnverifiedConsent: number;
  /** Selected brokers the server dropped as no longer active members. */
  brokerUnmatched?: number;
}

/** countAudience counters: the freeze counters, computed without a freeze. */
export type EdmAudienceCounters = EdmFreezeCounters;

/** A saved audience spec (companies/{c}/edm_segments). Selecting one copies its spec. */
export interface EdmSegment {
  id: string;
  name: string;
  spec: EdmAudience;
  updatedAt: string | null;
}

export type EdmTagKind = 'tag' | 'category' | 'event';

/** Tag registry entry, or a tag found on contacts but not registered (registered: false). */
export interface EdmTag {
  name: string;
  kind: EdmTagKind;
  eventDate: string | null;
  registered?: boolean;
}

export interface EdmCampaignProjection {
  id: string;
  name: string;
  status: EdmCampaignStatus;
  subject: string | null;
  contentPath: EdmContentPath | null;
  audience: EdmAudience | null;
  counters: EdmFreezeCounters | null;
  scheduledAt: string | null;
  frozenAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string | null;
  pausedReason: string | null;
  createdBy: { uid: string } | null;
  frozenScope: unknown;
  fromDomain: string | null;
  /** Send as this broker (Auth UID); null/absent = send as the company. */
  sendAsBrokerUid?: string | null;
  sentCount: number;
  totalCount: number;
  /** Saved content for the chosen path; the other path is null. */
  fixedLayout: EdmFixedLayout | null;
  customHtml: string | null;
  // Plan-v3 core, optional (absent = Phase 1c):
  /** edm_content: the block list the HTML was compiled from. */
  blocks?: EdmBlock[] | null;
  /** edm_preferences: the topic this campaign is sent under. */
  topicId?: string | null;
  /** edm_tracking: A/B subject test settings and the decided winner. */
  abTest?: EdmAbTest | null;
  abWinner?: EdmAbVariant | null;
  // Plan JOURNEYS (present only on a journey step template or a journey child):
  /** The journey this step template (status 'journey_step') or child email belongs to. */
  journeyId?: string | null;
  stepId?: string | null;
  /** Child only: the Brisbane day it sends on. */
  journeyDay?: string | null;
}

/** getStats counters. `null` means unknown (delivery tracking not live yet). */
export interface EdmStats {
  sent: number;
  delivered: number | null;
  bounced: number | null;
  complained: number | null;
  providerSuppressed: number | null;
  /** Sends the provider accepted and then failed (webhook `email.failed`). */
  failedProvider: number | null;
  unsubscribed: number | null;
  queued: number;
  pending: number;
  unknown: number;
  failed: number;
  blockedSuppressed: number;
  blockedConsentWithdrawn: number;
  blockedOutOfScope: number;
  blockedSourceMissing: number;
  failedInvalidRecipient: number;
  excludedInvalidEmail: number;
  /** edm_tracking: present only when the campaign's tracking was frozen at schedule. */
  engagement?: EdmEngagement | null;
}

export interface EdmPreview {
  subject: string;
  html: string;
  text: string;
}

export interface EdmUpdateFields {
  campaignId: string;
  name?: string;
  subject?: string;
  contentPath: EdmContentPath;
  fixedLayout?: EdmFixedLayout;
  customHtml?: string;
  audience: EdmAudience;
  /** A broker's Auth UID, '' to clear (send as the company); omit when unchanged. */
  sendAsBrokerUid?: string;
  // Plan-v3 core, sent only when the matching flag is on (null clears):
  /** edm_content: compiled to customHtml by the server (contentPath 'custom_html'). */
  blocks?: EdmBlock[] | null;
  /** edm_tracking. */
  abTest?: EdmAbTest | null;
  /** edm_preferences. */
  topicId?: string | null;
}

/** One `edmCampaignAction` request (shared with useEdmContent). */
export async function callEdmCampaignAction<Res>(action: string, payload: Record<string, unknown>): Promise<Res> {
  const fn = httpsCallable<Record<string, unknown>, Res>(
    functions,
    'edmCampaignAction',
    EDM_CALLABLE_OPTIONS,
  );
  const res = await fn({ action, ...payload });
  return res.data;
}

export interface EdmCampaignApi {
  listCampaigns: (page?: number, pageSize?: number) => Promise<{ campaigns: EdmCampaignProjection[]; total: number }>;
  getCampaign: (campaignId: string) => Promise<EdmCampaignProjection>;
  create: (name: string) => Promise<{ campaignId: string }>;
  update: (fields: EdmUpdateFields) => Promise<unknown>;
  preview: (campaignId: string) => Promise<EdmPreview>;
  sendTest: (campaignId: string) => Promise<{ ok: boolean }>;
  freeze: (campaignId: string) => Promise<unknown>;
  schedule: (campaignId: string, scheduledAt: string) => Promise<unknown>;
  pause: (campaignId: string) => Promise<unknown>;
  resume: (campaignId: string) => Promise<unknown>;
  cancel: (campaignId: string) => Promise<unknown>;
  getStats: (campaignId: string) => Promise<EdmStats>;
  /** Live count: the freeze's audience build with no ledger write. */
  countAudience: (audience: EdmAudience) => Promise<{ counters: EdmAudienceCounters }>;
  listSegments: () => Promise<{ segments: EdmSegment[] }>;
  saveSegment: (params: { segmentId?: string; name: string; spec: EdmAudience }) => Promise<{ id: string }>;
  deleteSegment: (segmentId: string) => Promise<unknown>;
  listTags: () => Promise<{ tags: EdmTag[] }>;
  /** Members the campaign may be sent as (server-filtered: active, sendable role, email). */
  listSenderCandidates: () => Promise<{ candidates: EdmSenderCandidate[] }>;
  // Plan-v3 core (each called only when its flag is on):
  /** edm_content: one page of the paged count; first page `cursor: ''`, then the previous page's cursor. */
  countAudiencePage: (
    audience: EdmAudience,
    page: { cursor: string; jobId?: string | null },
  ) => Promise<EdmAudienceCountPage>;
  /** edm_tracking: stats for up to 10 campaigns (any inaccessible id refuses all). */
  getStatsMany: (campaignIds: string[]) => Promise<{ stats: EdmCampaignStats[] }>;
  /** edm_tracking: a draft that resends a completed campaign to its non-openers. */
  createResend: (parentId: string, subject?: string) => Promise<{ campaignId: string }>;
}

/** Server cap on getStats campaignIds (compare). */
export const EDM_MAX_COMPARE = 10;

/** A team member the campaign may be sent as ("Send as"). */
export interface EdmSenderCandidate {
  uid: string;
  displayName: string;
}

/** `edmCampaignAction` bound to one company (shared by useEdmCampaigns and useEdmContent). */
export function useEdmCompanyCall(
  companyId: string | undefined,
): <Res>(action: string, payload?: Record<string, unknown>) => Promise<Res> {
  return useCallback(
    <Res,>(action: string, payload: Record<string, unknown> = {}) => {
      if (!companyId) return Promise.reject(new Error('companyId required'));
      return callEdmCampaignAction<Res>(action, { companyId, ...payload });
    },
    [companyId],
  );
}

/** The callable surface bound to one company. */
export function useEdmCampaigns(companyId: string | undefined): EdmCampaignApi {
  const withCompany = useEdmCompanyCall(companyId);

  return useMemo<EdmCampaignApi>(
    () => ({
      listCampaigns: (page = 1, pageSize = 25) => withCompany('listCampaigns', { page, pageSize }),
      getCampaign: (campaignId) => withCompany('getCampaign', { campaignId }),
      create: async (name) => {
        // The contract names the create field but not the response key; the
        // server returns the new campaign's id as `campaignId` (or `id`).
        const res = await withCompany<{ campaignId?: string; id?: string }>('create', { name });
        const campaignId = res?.campaignId ?? res?.id;
        if (!campaignId) throw new Error('create returned no campaign id');
        return { campaignId };
      },
      update: (fields) => withCompany('update', { ...fields }),
      preview: (campaignId) => withCompany('preview', { campaignId }),
      sendTest: (campaignId) => withCompany('sendTest', { campaignId }),
      freeze: (campaignId) => withCompany('freeze', { campaignId }),
      schedule: (campaignId, scheduledAt) => withCompany('schedule', { campaignId, scheduledAt }),
      pause: (campaignId) => withCompany('pause', { campaignId }),
      resume: (campaignId) => withCompany('resume', { campaignId }),
      cancel: (campaignId) => withCompany('cancel', { campaignId }),
      getStats: (campaignId) => withCompany('getStats', { campaignId }),
      countAudience: (audience) => withCompany('countAudience', { audience }),
      listSegments: () => withCompany('listSegments'),
      saveSegment: ({ segmentId, name, spec }) =>
        withCompany('saveSegment', { ...(segmentId ? { segmentId } : {}), name, spec }),
      deleteSegment: (segmentId) => withCompany('deleteSegment', { segmentId }),
      listTags: () => withCompany('listTags'),
      listSenderCandidates: () => withCompany('listSenderCandidates'),
      countAudiencePage: (audience, { cursor, jobId }) =>
        withCompany('countAudience', { audience, cursor, ...(jobId ? { jobId } : {}) }),
      getStatsMany: async (campaignIds) => {
        if (campaignIds.length === 0 || campaignIds.length > EDM_MAX_COMPARE) {
          throw new Error(`getStatsMany takes 1-${EDM_MAX_COMPARE} campaign ids`);
        }
        const res = await withCompany<{ stats?: EdmCampaignStats[] }>('getStats', { campaignIds });
        return { stats: Array.isArray(res?.stats) ? res.stats : [] };
      },
      createResend: async (parentId, subject) => {
        const res = await withCompany<{ campaignId?: string }>('createResend', {
          parentId,
          ...(subject && subject.trim() ? { subject } : {}),
        });
        if (!res?.campaignId) throw new Error('createResend returned no campaign id');
        return { campaignId: res.campaignId };
      },
    }),
    [withCompany],
  );
}
